import { useMemo } from "react";

import { useNodeStyle } from "@/components/expression/utils/NodeStyleProvider";

import type { NodeSize } from "@corca/data";

type PushClassNames = {
  positive: string;
  negative: string;
};

type ShiftType = "default" | "ascending" | "descending";

const CUSTOM_LOWERCASE_SYMBOLS: string[] = ["ℇ"];

const ASCENDING_SYMBOLS = [
  "t",
  "l",
  "i",
  "ƒ",
  "d",
  "h",
  "k",
  "b",
  "β",
  "δ",
  "θ",
  "λ",
  "f",
  "ζ",
  "ħ",
];

const DESCENDING_SYMBOLS = ["q", "g", "p", "y", "γ", "ρ", "η", "μ"];

/**
 * Vertical-nudge class names, keyed by node size and symbol class.
 *
 * The nudge magnitudes live in CSS custom properties
 * (`--symbol-shift-<size>-<type>`, defined in `theme.css`), so they can be
 * tuned globally — or overridden on a subtree, as the font playground at
 * `/dev/font-playground` does — without touching this map.
 *
 * Every class name is spelled out in full because Tailwind v4 only generates
 * utilities it can find verbatim in the source; a name built by interpolating
 * `size`/`shiftType` would never be emitted.
 */
const PUSH_CLASS_NAMES: Record<NodeSize, Record<ShiftType, PushClassNames>> = {
  sm: {
    default: {
      positive: "translate-y-(--symbol-shift-sm-default)",
      negative: "-translate-y-(--symbol-shift-sm-default)",
    },
    ascending: {
      positive: "translate-y-(--symbol-shift-sm-ascending)",
      negative: "-translate-y-(--symbol-shift-sm-ascending)",
    },
    descending: {
      positive: "translate-y-(--symbol-shift-sm-descending)",
      negative: "-translate-y-(--symbol-shift-sm-descending)",
    },
  },
  xs: {
    default: {
      positive: "translate-y-(--symbol-shift-xs-default)",
      negative: "-translate-y-(--symbol-shift-xs-default)",
    },
    ascending: {
      positive: "translate-y-(--symbol-shift-xs-ascending)",
      negative: "-translate-y-(--symbol-shift-xs-ascending)",
    },
    descending: {
      positive: "translate-y-(--symbol-shift-xs-descending)",
      negative: "-translate-y-(--symbol-shift-xs-descending)",
    },
  },
  xxs: {
    default: {
      positive: "translate-y-(--symbol-shift-xxs-default)",
      negative: "-translate-y-(--symbol-shift-xxs-default)",
    },
    ascending: {
      positive: "translate-y-(--symbol-shift-xxs-ascending)",
      negative: "-translate-y-(--symbol-shift-xxs-ascending)",
    },
    descending: {
      positive: "translate-y-(--symbol-shift-xxs-descending)",
      negative: "-translate-y-(--symbol-shift-xxs-descending)",
    },
  },
  xxxs: {
    default: {
      positive: "translate-y-(--symbol-shift-xxxs-default)",
      negative: "-translate-y-(--symbol-shift-xxxs-default)",
    },
    ascending: {
      positive: "translate-y-(--symbol-shift-xxxs-ascending)",
      negative: "-translate-y-(--symbol-shift-xxxs-ascending)",
    },
    descending: {
      positive: "translate-y-(--symbol-shift-xxxs-descending)",
      negative: "-translate-y-(--symbol-shift-xxxs-descending)",
    },
  },
};

function isEnclosedAlphanumeric(str: string): boolean {
  // Enclosed Alphanumerics (U+2460–U+24FF): circled/parenthesized letters & digits
  // Enclosed Alphanumeric Supplement (U+1F100–U+1F1FF): more circled/squared forms
  const cp = str.codePointAt(0)!;
  return (cp >= 0x2460 && cp <= 0x24ff) || (cp >= 0x1f100 && cp <= 0x1f1ff);
}

function isLowercaseSymbol(str: string): boolean {
  return /^\p{Ll}/u.test(str) || CUSTOM_LOWERCASE_SYMBOLS.includes(str);
}

function isAscendingSymbol(str: string): boolean {
  return ASCENDING_SYMBOLS.includes(str.normalize("NFKC")) && !isEnclosedAlphanumeric(str);
}

function isDescendingSymbol(str: string): boolean {
  return DESCENDING_SYMBOLS.includes(str.normalize("NFKC")) && !isEnclosedAlphanumeric(str);
}

/**
 * Returns the vertical nudge applied to lowercase symbols so their optical
 * centre lines up with the rest of the expression.
 *
 * `positive`/`negative` are the two directions consumers need: text is pushed
 * up (`negative`) while the selection/edit underline is pushed down
 * (`positive`) by the same amount.
 */
export function useSymbolPushTailwindClassName(str: string = ""): PushClassNames | undefined {
  const { view, size } = useNodeStyle();

  return useMemo(() => {
    if (!str || !isLowercaseSymbol(str) || view === "text") {
      return undefined;
    }

    let shiftType: ShiftType = "default";

    if (isAscendingSymbol(str)) {
      shiftType = "ascending";
    }

    if (isDescendingSymbol(str)) {
      shiftType = "descending";
    }

    return PUSH_CLASS_NAMES[size][shiftType];
  }, [str, view, size]);
}
